import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Save, X, Menu, ExternalLink, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARENT_OPTIONS = [
  { value: '', label: 'Raíz (menú principal)' },
  { value: 'eventos', label: 'Eventos (submenú)' },
  { value: 'informacion', label: 'Información (submenú)' },
];

export function NavigationManager({ authHeader }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: '', url: '', parent: '', order: 0, active: true, open_new_tab: false });

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/admin/navigation`, { headers: { Authorization: authHeader } });
      setItems(res.data);
    } catch { toast.error('Error al cargar navegación'); }
  };

  useEffect(() => { fetchItems(); }, []);

  const openCreate = () => {
    setEditing('new');
    setForm({ label: '', url: '', parent: 'eventos', order: items.length, active: true, open_new_tab: false });
  };

  const openEdit = (item) => {
    setEditing(item.id);
    setForm({ label: item.label, url: item.url, parent: item.parent || '', order: item.order, active: item.active, open_new_tab: item.open_new_tab || false });
  };

  const handleSave = async () => {
    if (!form.label || !form.url) {
      toast.error('Nombre y URL son obligatorios');
      return;
    }
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/navigation`, form, { headers: { Authorization: authHeader } });
        toast.success('Item de menú creado');
      } else {
        await axios.put(`${API}/admin/navigation/${editing}`, form, { headers: { Authorization: authHeader } });
        toast.success('Item actualizado');
      }
      setEditing(null);
      fetchItems();
    } catch { toast.error('Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este item del menú?')) return;
    try {
      await axios.delete(`${API}/admin/navigation/${id}`, { headers: { Authorization: authHeader } });
      toast.success('Item eliminado');
      fetchItems();
    } catch { toast.error('Error al eliminar'); }
  };

  // Group by parent
  const rootItems = items.filter(i => !i.parent);
  const eventItems = items.filter(i => i.parent === 'eventos');
  const infoItems = items.filter(i => i.parent === 'informacion');

  return (
    <div data-testid="navigation-manager">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-white font-medium flex items-center gap-2">
          <Menu size={18} className="text-[var(--primary)]" />
          Gestión de Navegación
        </h3>
        <Button onClick={openCreate} className="btn-primary" data-testid="create-nav-btn">
          <Plus size={16} className="mr-2" /> Nuevo Item
        </Button>
      </div>

      <p className="text-white/40 text-sm mb-6">
        Añade items personalizados a los menús de navegación. Los items se añaden a los menús existentes (Eventos, Información) o como items de raíz.
      </p>

      {/* Edit/Create Form */}
      {editing && (
        <div className="bg-[var(--surface)] border border-[var(--primary)]/30 p-6 mb-6" data-testid="nav-form">
          <h4 className="text-white font-medium mb-4">{editing === 'new' ? 'Nuevo Item de Menú' : 'Editar Item'}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">Nombre *</label>
              <Input
                value={form.label}
                onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder="óh!pera summer"
                className="bg-transparent border-white/20 text-white rounded-none"
                data-testid="nav-label-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">URL *</label>
              <Input
                value={form.url}
                onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
                placeholder="/coleccion/ohpera-summer"
                className="bg-transparent border-white/20 text-white rounded-none font-mono text-sm"
                data-testid="nav-url-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">Menú padre</label>
              <select
                value={form.parent}
                onChange={(e) => setForm(f => ({ ...f, parent: e.target.value }))}
                className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 outline-none"
                data-testid="nav-parent-select"
              >
                {PARENT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value} className="bg-[var(--surface)]">{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">Orden</label>
              <Input type="number" value={form.order} onChange={(e) => setForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))}
                className="bg-transparent border-white/20 text-white rounded-none w-20" />
            </div>
          </div>
          <div className="flex items-center gap-6 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} className="w-4 h-4 accent-[var(--primary)]" />
              <span className="text-white/70 text-sm">Activo</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.open_new_tab} onChange={(e) => setForm(f => ({ ...f, open_new_tab: e.target.checked }))} className="w-4 h-4 accent-[var(--primary)]" />
              <span className="text-white/70 text-sm">Abrir en nueva pestaña</span>
            </label>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} className="btn-primary" data-testid="save-nav-btn">
              <Save size={14} className="mr-1" /> Guardar
            </Button>
            <Button variant="ghost" onClick={() => setEditing(null)} className="text-white/50">
              <X size={14} className="mr-1" /> Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Items grouped by section */}
      {items.length === 0 && !editing ? (
        <div className="text-center py-12 bg-[var(--surface)] border border-white/5">
          <Menu size={48} className="text-white/10 mx-auto mb-4" />
          <p className="text-white/40">No hay items personalizados en el menú</p>
        </div>
      ) : (
        <div className="space-y-6">
          {eventItems.length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-widest text-[var(--primary)] mb-2">Submenú Eventos</h4>
              <div className="space-y-1">{eventItems.map(item => <NavItemRow key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} />)}</div>
            </div>
          )}
          {infoItems.length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-widest text-[var(--primary)] mb-2">Submenú Información</h4>
              <div className="space-y-1">{infoItems.map(item => <NavItemRow key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} />)}</div>
            </div>
          )}
          {rootItems.length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-widest text-[var(--primary)] mb-2">Menú Principal (Raíz)</h4>
              <div className="space-y-1">{rootItems.map(item => <NavItemRow key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} />)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NavItemRow({ item, onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-[var(--surface)] border border-white/5 hover:border-white/10 transition-colors" data-testid={`nav-item-${item.id}`}>
      <ChevronRight size={14} className="text-white/20" />
      <div className="flex-1 min-w-0">
        <span className="text-white text-sm">{item.label}</span>
        <span className="text-white/30 text-xs ml-2 font-mono">{item.url}</span>
        {!item.active && <span className="text-xs text-red-400 ml-2">inactivo</span>}
        {item.open_new_tab && <ExternalLink size={10} className="inline ml-1 text-white/30" />}
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onEdit(item)} className="p-1.5 text-white/30 hover:text-white transition-colors"><Edit2 size={14} /></button>
        <button onClick={() => onDelete(item.id)} className="p-1.5 text-white/30 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}
